import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  IconPlus,
  IconPencil,
  IconTrash,
  IconGripVertical,
  IconArchive,
  IconArchiveOff
} from '@tabler/icons-react';
import { resolveBudgetIcon } from '../utils/budgetIcon';
import api from '../services/api';
import { balancesFromTangible } from '../utils/tangibleBalances';
import { useGroup } from '../contexts/GroupContext.jsx';
import { useCanEdit, useIsOwner } from '../components/RoleGate.jsx';

import { MaskedCurrency } from '../components/MaskedValue';
import { resolveAmountColor } from '../components/AmountDisplay';
import { PageHero, SectionContainer, Row, ActionBar } from '../components/ds';
import { BudgetRunCaption } from '../components/DashboardGroups';
import { CardSkeleton, BalanceSkeleton } from '../components/SkeletonLoader';
import { CenteredLoader, InlineLoader } from '../components/LoadingIndicator';
import { useToast } from '../components/ToastNotification';
import { SectionErrorBoundary } from '../components/ErrorBoundary';
import { useBudgetFormValidation } from '../hooks/useFormValidation';
import { invalidateListCache, BUDGETS_LIST_CACHE_KEY } from '../utils/listCacheUtil';
import { useEngineStrings } from '../user_guides/i18n/useEngineStrings';
import { interpolate } from '../utils/entryType';
import {
  acronymOf,
  byName,
  DEFAULT_ACRONYM,
  groupBudgetsByCategory,
  makeBudgetAcronymOf,
  makeCategoryIdOf,
  withAcronym,
} from '../utils/budgetGroups';
import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  defaultDropAnimationSideEffects,
} from '@dnd-kit/core';
import { DraggableBudget, DroppableCard } from '../components/budgets/BudgetCategoryDnd';
import Segmented from '../components/ds/Segmented';
import HorizonChip from '../components/ds/HorizonChip';
import HorizonSwitchDialog from '../components/HorizonSwitchDialog';
import ArchiveDialog from '../components/ArchiveDialog';
import { horizonOf, HORIZON_MONTHLY } from '../utils/budgetsByCategory';

// Types limited to one per workspace (#933: only Future Expenses now). Used to
// disable the matching create-dialog option so the UI matches the backend
// one-per-workspace rule for type 2.
const SINGULAR_TYPES = [2];

// Select value for Default in the Edit budget dialog: Default is not a row, so
// it has no budgetCategoryId of its own (#1963).
const DEFAULT_OPTION = '__default__';

// A Normal budget's horizon (#2141): absent on the row reads as Month to
// month (`horizonOf`), so the dialog seeds from it and sends the field only
// on a change.

// #1964 drag activation (DESIGN_SYSTEM.md section 8.16): drag is always on,
// so a hold, never a tap, lifts a row. Touch waits longer, so a finger that
// rests a moment before scrolling still scrolls the page.
const MOUSE_ACTIVATION = { delay: 250, tolerance: 5 };
const TOUCH_ACTIVATION = { delay: 400, tolerance: 5 };

// Masonry weight of a card: its rows plus the header's share, so a short card
// with a header still counts for more than nothing.
const cardWeight = (rowCount) => rowCount + 2;

const Budgets = ({ isAuthReady = false }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { palette, ds } = theme;
  // v4 (#1377): xs gets the pinned ActionBar add button; sm+ puts the
  // budget-fab anchor on the first card's [+]. JS-conditional so the anchor
  // exists exactly once in the DOM per viewport.
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isWide = useMediaQuery(theme.breakpoints.up('md'));
  const { activeGroupId } = useGroup();
  const canEdit = useCanEdit();
  const isOwner = useIsOwner();
  const strings = useEngineStrings();
  const { showSuccess, showError, showWarning } = useToast();
  const [budgets, setBudgets] = useState([]);
  const [archivedBudgets, setArchivedBudgets] = useState([]);
  const [budgetCategories, setBudgetCategories] = useState([]);
  const [budgetBalances, setBudgetBalances] = useState({});
  const [loading, setLoading] = useState(true);
  const [balancesLoading, setBalancesLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  // The category a card's [+] presets; null files the new budget in Default.
  const [createBudgetCategoryId, setCreateBudgetCategoryId] = useState(null);

  const [editDialog, setEditDialog] = useState({
    open: false, budget: null, newName: '', budgetCategoryId: DEFAULT_OPTION, horizon: HORIZON_MONTHLY, error: '', submitting: false
  });
  const [categoryDialog, setCategoryDialog] = useState({
    open: false, budgetCategory: null, name: '', error: '', submitting: false
  });
  // #2175: the Normal row whose horizon chip was pressed; null keeps the dialog closed.
  const [horizonBudget, setHorizonBudget] = useState(null);
  // #1964 drag between categories (always on for editors).
  const [activeDragId, setActiveDragId] = useState(null);
  // Card key -> column index for this page view, so a drop (or any change in
  // card size) never moves a card to another column. Reset when the column
  // count or the workspace changes; the next visit re-balances.
  const stickyColumnsRef = useRef({ count: 0, map: {} });
  // The click that can follow a drop must not open the budget.
  const justDraggedRef = useRef(false);
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: MOUSE_ACTIVATION }),
    useSensor(TouchSensor, { activationConstraint: TOUCH_ACTIVATION })
  );
  const [deleteCategoryDialog, setDeleteCategoryDialog] = useState({
    open: false, budgetCategory: null, memberCount: 0, submitting: false
  });
  // #2159: category is the row's icon-box category, so the dialog's drawing
  // matches the row the Archive press came from.
  const [archiveDialog, setArchiveDialog] = useState({
    budget: null, category: 'budgets', submitting: false
  });
  const [unarchivingId, setUnarchivingId] = useState(null);

  const {
    data: newBudget,
    errors: validationErrors,
    updateField,
    touchField,
    validateAll,
    isValid,
    reset: resetForm
  } = useBudgetFormValidation({ name: '', type: 0 });

  const CACHE_KEY = 'budgets_cache';
  const CACHE_TTL = 60 * 1000;

  const getCachedData = useCallback(() => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (!cached) return null;
      const { data, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp > CACHE_TTL) {
        localStorage.removeItem(CACHE_KEY);
        return null;
      }
      return data;
    } catch (err) {
      console.warn('Cache read error:', err);
      return null;
    }
  }, [CACHE_KEY, CACHE_TTL]);

  const setCachedData = (active, archived, categories, balances) => {
    try {
      const cacheData = {
        data: { budgets: active, archivedBudgets: archived, budgetCategories: categories, balances },
        timestamp: Date.now()
      };
      localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
    } catch (err) {
      console.warn('Cache write error:', err);
    }
  };

  const invalidateBudgetsCache = useCallback(() => {
    localStorage.removeItem(CACHE_KEY);
    invalidateListCache(BUDGETS_LIST_CACHE_KEY);
  }, [CACHE_KEY]);

  const loadBudgets = useCallback(async () => {
    try {
      setLoading(true);
      setBalancesLoading(true);
      setError(null);

      const cachedData = getCachedData();
      if (cachedData) {
        setBudgets(cachedData.budgets || []);
        setArchivedBudgets(cachedData.archivedBudgets || []);
        setBudgetCategories(cachedData.budgetCategories || []);
        setBudgetBalances(cachedData.balances || {});
        setLoading(false);
        setBalancesLoading(false);
        return;
      }

      if (!activeGroupId) return;
      const response = await api.get(activeGroupId, '/budgets?includeArchived=true');
      const budgetsData = response.budgets || [];

      const visible = budgetsData.filter(b => b.budgetId !== '0');
      const active = visible.filter(b => !b.archived);
      const archived = visible.filter(b => b.archived);
      // #1962: the category list rides on the same response.
      const categories = response.budgetCategories || [];

      setBudgets(active);
      setArchivedBudgets(archived);
      setBudgetCategories(categories);
      setLoading(false);

      // #2158: active balances ride one /tangible read (active-only since
      // #2156). #1948: archived budgets load their balance too, for display
      // only, and keep their per-entity calls because /tangible omits them.
      const tangiblePromise = api.get(activeGroupId, '/tangible').catch((err) => {
        console.warn('Failed to load budget balances:', err);
        showWarning('Failed to load budget balances', { duration: 3000 });
        return null;
      });
      const archivedPromises = archived.map(async (budget) => {
        try {
          const res = await api.get(activeGroupId, `/budgets/${encodeURIComponent(budget.budgetId)}/balance`);
          return { id: budget.budgetId, balance: (res?.balanceCents ?? 0) / 100 };
        } catch (err) {
          console.warn('Failed to load balance for budget %s:', budget.budgetId, err);
          showWarning(`Failed to load balance for ${budget.name}`, { duration: 3000 });
          return { id: budget.budgetId, balance: 0 };
        }
      });

      const [tangible, archivedResults] = await Promise.all([tangiblePromise, Promise.all(archivedPromises)]);
      const { budgets: activeCents } = balancesFromTangible(tangible);
      const balances = {};
      active.forEach((budget) => {
        balances[budget.budgetId] = (activeCents[budget.budgetId] ?? 0) / 100;
      });
      archivedResults.forEach(result => {
        balances[result.id] = result.balance;
      });
      setBudgetBalances(balances);
      setBalancesLoading(false);

      setCachedData(active, archived, categories, balances);
    } catch (err) {
      console.error('Failed to load budgets:', err);
      const errorMessage = err.message || 'Failed to load budgets. Please try again.';
      setError(errorMessage);
      showError(errorMessage);
      setLoading(false);
      setBalancesLoading(false);
    }
  }, [activeGroupId, getCachedData, showError, showWarning]);

  useEffect(() => {
    if (isAuthReady && activeGroupId) {
      loadBudgets();
    }
  }, [loadBudgets, isAuthReady, activeGroupId]);

  // Column positions belong to one workspace's page view.
  useEffect(() => {
    stickyColumnsRef.current = { count: 0, map: {} };
  }, [activeGroupId]);

  const handleCreateBudget = async () => {
    if (!validateAll()) {
      showWarning('Please fix the validation errors before creating the budget');
      return;
    }

    // Only Normal budgets join a category (epic #1960 decision 8): choosing
    // Pending Income or Planned Expenses drops the preset.
    const payload = newBudget.type === 0 && createBudgetCategoryId
      ? { ...newBudget, budgetCategoryId: createBudgetCategoryId }
      : newBudget;

    try {
      setCreating(true);
      const res = await api.post(activeGroupId, '/budgets', payload);
      invalidateBudgetsCache();
      setCreateDialogOpen(false);
      setCreateBudgetCategoryId(null);
      showSuccess(`Budget "${newBudget.name}" created successfully`);
      resetForm({ name: '', type: 0 });
      // Land on the new budget's detail page so the user can use it straight
      // away (and the guide's budget steps have their anchors present),
      // consistent with ledger create (#817). Fall back to the list if the
      // id is missing.
      const newBudgetId = res && res.budget && res.budget.budgetId;
      if (newBudgetId) {
        navigate(`/budgets/${encodeURIComponent(newBudgetId)}`);
      } else {
        loadBudgets();
      }
    } catch (err) {
      console.error('Failed to create budget:', err);
      if (err.body?.code === 'budget_category_missing') {
        handleCategoryMissing();
        handleCloseDialog();
      } else if (err.response?.data?.error?.type === 'VALIDATION_ERROR') {
        showError(`Validation Error: ${err.response.data.error.message}`);
      } else if (err.response?.data?.error?.type === 'CONFLICT_ERROR') {
        showError('A budget with this name already exists. Please choose a different name.');
      } else {
        showError(err.message || 'Failed to create budget. Please try again.');
      }
    } finally {
      setCreating(false);
    }
  };

  const openCreate = (budgetCategoryId = null) => {
    setCreateBudgetCategoryId(budgetCategoryId);
    setCreateDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setCreateDialogOpen(false);
    setCreateBudgetCategoryId(null);
    resetForm({ name: '', type: 0 });
  };

  // A category deleted elsewhere since the list loaded (409
  // budget_category_missing, #1962): say so and redraw from the server.
  const handleCategoryMissing = () => {
    showError(strings['budgetCategories.missing']);
    invalidateBudgetsCache();
    loadBudgets();
  };

  // A dangling budgetCategoryId files under Default (shared rule, #1971).
  const categoryIdOf = makeCategoryIdOf(budgetCategories);

  const openEdit = (budget) => {
    setEditDialog({
      open: true,
      budget,
      newName: budget.name,
      budgetCategoryId: categoryIdOf(budget) ?? DEFAULT_OPTION,
      horizon: horizonOf(budget),
      error: '',
      submitting: false
    });
  };

  const closeEdit = () => {
    setEditDialog({ open: false, budget: null, newName: '', budgetCategoryId: DEFAULT_OPTION, horizon: HORIZON_MONTHLY, error: '', submitting: false });
  };

  const handleEdit = async () => {
    const { budget, newName, budgetCategoryId, horizon } = editDialog;
    const trimmed = (newName || '').trim();
    if (!trimmed) {
      setEditDialog(prev => ({ ...prev, error: strings['budgetCategories.nameRequired'] }));
      return;
    }
    // Send only what changed; the category and horizon only for Normal budgets.
    const body = { version: budget.version };
    if (trimmed !== budget.name) body.name = trimmed;
    const nextCategoryId = budgetCategoryId === DEFAULT_OPTION ? null : budgetCategoryId;
    if (budget.type === 0 && nextCategoryId !== categoryIdOf(budget)) {
      body.budgetCategoryId = nextCategoryId;
    }
    if (budget.type === 0 && horizon !== horizonOf(budget)) {
      body.horizon = horizon;
    }
    if (Object.keys(body).length === 1) {
      closeEdit();
      return;
    }
    try {
      setEditDialog(prev => ({ ...prev, submitting: true, error: '' }));
      await api.put(activeGroupId, `/budgets/${encodeURIComponent(budget.budgetId)}`, body);
      invalidateBudgetsCache();
      if (body.name) showSuccess(`Renamed to "${trimmed}"`);
      closeEdit();
      loadBudgets();
    } catch (err) {
      console.error('Failed to update budget:', err);
      if (err.body?.code === 'budget_category_missing') {
        closeEdit();
        handleCategoryMissing();
      } else {
        setEditDialog(prev => ({ ...prev, submitting: false, error: err.message || 'Save failed.' }));
      }
    }
  };

  // Category name dialog: create when budgetCategory is null, else rename.
  const openCategoryDialog = (budgetCategory = null) => {
    setCategoryDialog({
      open: true, budgetCategory, name: budgetCategory ? budgetCategory.name : '', error: '', submitting: false
    });
  };

  const closeCategoryDialog = () => {
    setCategoryDialog({ open: false, budgetCategory: null, name: '', error: '', submitting: false });
  };

  const handleSaveCategory = async () => {
    const { budgetCategory, name } = categoryDialog;
    const trimmed = (name || '').trim();
    if (!trimmed) {
      setCategoryDialog(prev => ({ ...prev, error: strings['budgetCategories.nameRequired'] }));
      return;
    }
    if (budgetCategory && trimmed === budgetCategory.name) {
      closeCategoryDialog();
      return;
    }
    try {
      setCategoryDialog(prev => ({ ...prev, submitting: true, error: '' }));
      if (budgetCategory) {
        await api.put(
          activeGroupId,
          `/budget-categories/${encodeURIComponent(budgetCategory.budgetCategoryId)}`,
          { name: trimmed, version: budgetCategory.version }
        );
      } else {
        await api.post(activeGroupId, '/budget-categories', { name: trimmed });
      }
      invalidateBudgetsCache();
      closeCategoryDialog();
      loadBudgets();
    } catch (err) {
      console.error('Failed to save category:', err);
      // The server's message names the rule (duplicate, reserved, cap, stale).
      setCategoryDialog(prev => ({ ...prev, submitting: false, error: err.message || 'Save failed.' }));
      // A rename can fail on a stale version or a category deleted elsewhere:
      // refresh the list underneath so the next try has the current row.
      if (budgetCategory && (err.status === 409 || err.status === 404)) {
        invalidateBudgetsCache();
        loadBudgets();
      }
    }
  };

  const openDeleteCategory = (budgetCategory, memberCount) => {
    setDeleteCategoryDialog({ open: true, budgetCategory, memberCount, submitting: false });
  };

  const closeDeleteCategory = () => {
    setDeleteCategoryDialog({ open: false, budgetCategory: null, memberCount: 0, submitting: false });
  };

  const handleDeleteCategory = async () => {
    const { budgetCategory } = deleteCategoryDialog;
    try {
      setDeleteCategoryDialog(prev => ({ ...prev, submitting: true }));
      await api.delete(
        activeGroupId,
        `/budget-categories/${encodeURIComponent(budgetCategory.budgetCategoryId)}`
      );
    } catch (err) {
      // 404: already gone, which is the outcome asked for.
      if (err.status !== 404) {
        console.error('Failed to delete category:', err);
        showError(err.message || 'Failed to delete category.');
        setDeleteCategoryDialog(prev => ({ ...prev, submitting: false }));
        return;
      }
    }
    invalidateBudgetsCache();
    closeDeleteCategory();
    loadBudgets();
  };

  const openArchive = (budget, category) => {
    setArchiveDialog({ budget, category, submitting: false });
  };

  const closeArchive = () => {
    setArchiveDialog(prev => ({ ...prev, budget: null, submitting: false }));
  };

  const handleArchive = async () => {
    const { budget } = archiveDialog;
    try {
      setArchiveDialog(prev => ({ ...prev, submitting: true }));
      await api.delete(activeGroupId, `/budgets/${encodeURIComponent(budget.budgetId)}`);
      invalidateBudgetsCache();
      showSuccess(`Archived "${budget.name}"`);
      closeArchive();
      loadBudgets();
    } catch (err) {
      console.error('Failed to archive budget:', err);
      showError(err.message || 'Failed to archive budget.');
      setArchiveDialog(prev => ({ ...prev, submitting: false }));
    }
  };

  const handleUnarchive = async (budget) => {
    try {
      setUnarchivingId(budget.budgetId);
      await api.put(activeGroupId, `/budgets/${encodeURIComponent(budget.budgetId)}`, {
        archived: false,
        version: budget.version
      });
      invalidateBudgetsCache();
      showSuccess(`Restored "${budget.name}"`);
      loadBudgets();
    } catch (err) {
      console.error('Failed to unarchive budget:', err);
      showError(err.message || 'Failed to unarchive budget.');
    } finally {
      setUnarchivingId(null);
    }
  };

  // #1964: the move shows at once and saves behind it. A failed save puts the
  // budget back and says so; a 409 (stale version, or the category deleted
  // elsewhere) reloads the list from the server.
  const moveBudget = async (budget, budgetCategoryId) => {
    const previous = budget.budgetCategoryId;
    const setLink = (value, extra = {}) => setBudgets((prev) => prev.map((b) => (
      b.budgetId === budget.budgetId ? { ...b, budgetCategoryId: value, ...extra } : b
    )));
    setLink(budgetCategoryId);
    try {
      const res = await api.put(activeGroupId, `/budgets/${encodeURIComponent(budget.budgetId)}`, {
        budgetCategoryId,
        version: budget.version
      });
      const version = res?.budget?.version;
      setLink(budgetCategoryId, typeof version === 'number' ? { version } : {});
      invalidateBudgetsCache();
    } catch (err) {
      console.error('Failed to move budget:', err);
      setLink(previous);
      showError(interpolate(strings['budgetCategories.moveFailed'], { name: budget.name }));
      if (err.status === 409) {
        invalidateBudgetsCache();
        loadBudgets();
      }
    }
  };

  const handleDragStart = ({ active }) => setActiveDragId(active.id);

  const endDrag = () => {
    setActiveDragId(null);
    justDraggedRef.current = true;
    setTimeout(() => { justDraggedRef.current = false; }, 0);
  };

  const handleDragEnd = ({ active, over }) => {
    endDrag();
    // A miss: the overlay flies back and nothing is saved.
    if (!over) return;
    const budget = budgets.find((b) => b.budgetId === active.id);
    if (!budget || budget.type !== 0) return;
    const target = over.id === DEFAULT_OPTION ? null : over.id;
    // Dropped on its own card: same as a miss.
    if (target === categoryIdOf(budget)) return;
    moveBudget(budget, target);
  };

  const handleBudgetClick = (budgetId) => {
    if (justDraggedRef.current) return;
    navigate(`/budgets/${budgetId}`);
  };

  const activateOnKey = (e, fn) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      fn();
    }
  };

  // §11 value coloring via the pure resolver shared with AmountDisplay, rendered
  // through MaskedCurrency (an inheriting span) so the number keeps the Row /
  // section font scale instead of AmountDisplay's 16px Typography. Masking is
  // preserved by MaskedCurrency itself.
  const renderAmount = (dollars, variant) => {
    const value = typeof dollars === 'number' ? dollars : 0;
    const cents = Math.round(value * 100);
    return (
      <MaskedCurrency
        value={value}
        style={{ color: resolveAmountColor(palette, variant, cents < 0, cents === 0) }}
      />
    );
  };

  const balanceView = (budget) => {
    const rawBalance = budgetBalances[budget.budgetId];
    const isBalanceLoading = balancesLoading || typeof rawBalance !== 'number';
    // Pending Income (type=3): flip sign on display so a raw negative balance
    // reads as the positive amount the user is owed.
    const displayBalance = !isBalanceLoading && budget.type === 3
      ? -rawBalance
      : rawBalance;
    return { isBalanceLoading, displayBalance };
  };

  // category drives the icon-box color ('budgets' teal for General, 'income'
  // gold for Pending Income). shimmerIndex staggers each row's sheen sweep off
  // the shared cycle so no two start together.
  const renderBudgetRow = (budget, category = 'budgets', shimmerIndex = 0, options = {}) => {
    const { isBalanceLoading, displayBalance } = balanceView(budget);

    // v4 (#1377, convention 1): edit/archive reveal on row hover via the Row
    // primitive's action band - 15px muted glyphs per the board. Archive is
    // owner only (#2157), like ledgers.
    const ownerActions = canEdit ? (
      <Box sx={{ display: 'flex', gap: 0.25 }} onClick={(e) => e.stopPropagation()}>
        <Tooltip title={strings['budgetCategories.edit']}>
          <IconButton
            size="small"
            aria-label={interpolate(strings['budgetCategories.editAria'], { name: budget.name })}
            onClick={() => openEdit(budget)}
            sx={{ color: palette.text.secondary, padding: '2px' }}
          >
            <IconPencil size={15} />
          </IconButton>
        </Tooltip>
        {isOwner && (
          <Tooltip title="Archive">
            <IconButton
              size="small"
              aria-label={`Archive ${budget.name}`}
              onClick={() => openArchive(budget, category)}
              sx={{ color: palette.text.secondary, padding: '2px' }}
            >
              <IconArchive size={15} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    ) : undefined;

    // #1127: per-budget AI-picked icon (falls back to the placeholder).
    const BudgetIcon = resolveBudgetIcon(budget.icon);

    // #1964 lifted row (the DragOverlay copy): the grip stands in for the icon
    // box at the same size, with no actions and no navigation.
    if (options.overlay) {
      const boxSize = options.size === 'compact' ? ds.size.iconBoxCompact : ds.size.iconBox;
      return (
        <Row
          key={budget.budgetId}
          category={category}
          leadNode={
            <Box
              aria-hidden
              sx={{
                width: boxSize,
                height: boxSize,
                borderRadius: `${ds.radius.icon}px`,
                backgroundColor: palette.tint.interactive,
                color: palette.interactive,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <IconGripVertical size={options.size === 'compact' ? 14 : 16} />
            </Box>
          }
          name={budget.name}
          size={options.size}
          value={isBalanceLoading ? <BalanceSkeleton width="60px" /> : renderAmount(displayBalance, 'row')}
          divider={false}
          aria-label={budget.name}
        />
      );
    }

    return (
      <Row
        key={budget.budgetId}
        category={category}
        icon={<BudgetIcon />}
        name={budget.name}
        size={options.size}
        value={isBalanceLoading ? <BalanceSkeleton width="60px" /> : renderAmount(displayBalance, 'row')}
        actions={ownerActions}
        trailing={budget.type === 0 ? <HorizonChip horizon={horizonOf(budget)} onPress={canEdit ? () => setHorizonBudget(budget) : undefined} /> : undefined}
        actionsAfterValue
        shimmer
        shimmerDelay={`${(shimmerIndex * 1700) % 12000}ms`}
        // Inside a drag wrapper the wrapper carries the divider (#1964).
        divider={!options.inDraggable}
        onClick={() => handleBudgetClick(budget.budgetId)}
        role="button"
        tabIndex={0}
        aria-label={budget.name}
        onKeyDown={(e) => activateOnKey(e, () => handleBudgetClick(budget.budgetId))}
        {...(options.rowProps || {})}
      />
    );
  };

  const renderArchivedRow = (budget) => {
    const restoreAction = isOwner ? (
      <Box onClick={(e) => e.stopPropagation()}>
        <Tooltip title="Restore">
          <span>
            <IconButton
              size="small"
              aria-label={`Restore ${budget.name}`}
              onClick={() => handleUnarchive(budget)}
              disabled={unarchivingId === budget.budgetId}
              sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}
            >
              <IconArchiveOff size={18} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    ) : undefined;

    // #1127: per-budget AI-picked icon (falls back to the placeholder).
    const BudgetIcon = resolveBudgetIcon(budget.icon);
    const { isBalanceLoading, displayBalance } = balanceView(budget);

    return (
      <Row
        key={budget.budgetId}
        category="budgets"
        icon={<BudgetIcon />}
        name={budget.name}
        meta="Archived"
        value={isBalanceLoading ? <BalanceSkeleton width="60px" /> : renderAmount(displayBalance, 'row')}
        actions={restoreAction}
        actionsAfterValue
        onClick={() => handleBudgetClick(budget.budgetId)}
        role="button"
        tabIndex={0}
        aria-label={budget.name}
        onKeyDown={(e) => activateOnKey(e, () => handleBudgetClick(budget.budgetId))}
        sx={{ opacity: 0.7, '&:hover': { opacity: 0.9 } }}
      />
    );
  };

  if (!isAuthReady) {
    return <CenteredLoader message="Initializing..." />;
  }

  if (loading) {
    return (
      <Box sx={{ pb: 8 }}>
        <PageHero label="Budgets" />
        <Box sx={{ mt: { xs: '12px', md: '14px' } }}>
          <CardSkeleton count={2} />
        </Box>
      </Box>
    );
  }

  // Singular types already present in the workspace (active OR archived). Used
  // to disable those options in the create dialog so the UI matches the
  // backend one-per-workspace rule (#933: only Future Expenses).
  const usedSingularTypes = new Set(
    [...budgets, ...archivedBudgets]
      .filter((b) => SINGULAR_TYPES.includes(b.type))
      .map((b) => b.type)
  );

  // v4 (#1377, convention 5): the type-2 singleton is the "Planned Expenses"
  // section BELOW Pending Income - isolated from all aggregates (replaces the
  // #940 hero-aux click-card).
  const feBudget = budgets.find((b) => b.type === 2);

  const normalBudgets = budgets.filter((b) => b.type === 0);
  const pendingIncomeBudgets = budgets.filter((b) => b.type === 3);

  // #945: per-section subtotals. General sums raw balances; Pending Income
  // sign-flips per the same rule the PI rows use (a -balance shows as +balance).
  const sumBalances = (rows, signFlip = false) => rows.reduce((sum, b) => {
    const bal = budgetBalances[b.budgetId];
    if (typeof bal !== 'number') return sum;
    return sum + (signFlip ? -bal : bal);
  }, 0);
  const generalTotal = sumBalances(normalBudgets, false);
  const pendingIncomeTotal = sumBalances(pendingIncomeBudgets, true);
  // #1948: Archived subtotal counts Normal budgets only, the same rule as
  // "Total balance". Display only: Total balance stays active-only.
  const archivedTotal = sumBalances(archivedBudgets.filter((b) => b.type === 0), false);

  // v4 hero aux (#1377, convention 5): "Total balance" = every Normal budget,
  // across all categories and Default (Pending Income and Planned Expenses
  // never feed it). positiveStrong per section 11; a negative total falls back
  // to the negative token.
  const totalBlock = (
    <Box sx={{ textAlign: 'right' }}>
      <Typography component="div" sx={{ fontSize: '11px', color: palette.text.dim }}>
        Total balance
      </Typography>
      {balancesLoading ? (
        <Box sx={{ mt: '4px', display: 'flex', justifyContent: 'flex-end' }}>
          <BalanceSkeleton width="80px" />
        </Box>
      ) : (
        <Typography
          component="div"
          sx={{
            fontSize: '16px',
            fontWeight: ds.font.weightMedium,
            fontVariantNumeric: 'tabular-nums',
            color: generalTotal < 0 ? palette.error.main : palette.positiveStrong,
            marginTop: '2px',
          }}
        >
          <MaskedCurrency value={generalTotal} />
        </Typography>
      )}
    </Box>
  );

  // #1963: "+ Add category" sits far right of the title row, after the total.
  // #1963: "+ Add category" sits far right of the title row, after the total.
  const totalAux = canEdit ? (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: `${ds.space[5]}px` }}>
      {totalBlock}
      <Button
        variant="outlined"
        size="small"
        startIcon={<IconPlus size={14} />}
        onClick={() => openCategoryDialog(null)}
      >
        {strings['budgetCategories.add']}
      </Button>
    </Box>
  ) : totalBlock;

  // A workspace with categories but no budgets still shows its cards, each
  // with a [+]; only a workspace with nothing at all gets the empty state.
  const isEmpty = budgets.length === 0 && archivedBudgets.length === 0 && budgetCategories.length === 0;

  // #1963 cards: one per category plus Default, all sorted by name. Default
  // shows only when it holds budgets or no categories exist yet (epic #1960
  // decision 5), so a fresh workspace still has a [+] to tap. Shared with the
  // Dashboard (#1971).
  const cards = groupBudgetsByCategory(normalBudgets, budgetCategories, strings['budgetCategories.default']);
  // #2116: header acronyms show once the workspace has a category.
  const hasCategories = budgetCategories.length > 0;
  const budgetAcronymOf = makeBudgetAcronymOf(budgetCategories);

  const presetCategory = createBudgetCategoryId
    ? budgetCategories.find((c) => c.budgetCategoryId === createBudgetCategoryId)
    : null;

  const sectionDensity = isMobile ? 'compact' : 'default';
  let shimmerIndex = 0;

  const cardNode = (card, index) => {
    const { budgetCategory, members, monthly, planning, name } = card;
    const rowSize = isWide ? 'compact' : 'default';
    // Editors can hold any Normal row to drag it (#1964); viewers get plain rows.
    const rowOf = (b) => (canEdit
      ? (
        <DraggableBudget key={b.budgetId} id={b.budgetId} gripSize={rowSize}>
          {renderBudgetRow(b, 'budgets', shimmerIndex++, { size: rowSize, inDraggable: true })}
        </DraggableBudget>
      )
      : renderBudgetRow(b, 'budgets', shimmerIndex++, { size: rowSize })
    );
    // #2269, #2271, #2273: each run under its caption, Month to month first,
    // the Month to month sum on its caption row.
    const m2mSum = balancesLoading ? <BalanceSkeleton width="70px" /> : renderAmount(sumBalances(monthly, false), 'subtotal');
    const rows = [
      ...(monthly.length > 0 ? [<BudgetRunCaption key="run-monthly" data-testid="budget-run-monthly" aside={m2mSum}>{strings['budgets.horizonMonthly']}</BudgetRunCaption>] : []),
      ...monthly.map(rowOf),
      ...(planning.length > 0 ? [<BudgetRunCaption key="run-planning" data-testid="budget-run-planning">{strings['budgets.horizonPlanningAhead']}</BudgetRunCaption>] : []),
      ...planning.map(rowOf),
    ];
    // Rename and delete reveal on header hover (always shown on touch), so the
    // everyday page stays quiet. Default has neither (epic #1960 decision 5).
    const headerActions = canEdit && budgetCategory ? (
      <Box component="span" className="br-card-actions" sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
        <Tooltip title={strings['budgetCategories.renameTitle']}>
          <IconButton
            size="small"
            aria-label={interpolate(strings['budgetCategories.renameAria'], { name })}
            onClick={() => openCategoryDialog(budgetCategory)}
            sx={{ color: palette.text.secondary, padding: '2px' }}
          >
            <IconPencil size={15} />
          </IconButton>
        </Tooltip>
        <Tooltip title={strings['budgetCategories.delete']}>
          <IconButton
            size="small"
            aria-label={interpolate(strings['budgetCategories.deleteAria'], { name })}
            onClick={() => openDeleteCategory(budgetCategory, members.length)}
            sx={{ color: palette.text.secondary, padding: '2px' }}
          >
            <IconTrash size={15} />
          </IconButton>
        </Tooltip>
      </Box>
    ) : undefined;

    const dropId = budgetCategory ? budgetCategory.budgetCategoryId : DEFAULT_OPTION;
    const acronym = hasCategories ? (budgetCategory ? acronymOf(name) : DEFAULT_ACRONYM) : null;
    return (
      <DroppableCard key={card.key} id={dropId} disabled={!canEdit}>
        {(isOver) => (
          <SectionContainer
            title={withAcronym(name, acronym)}
            count={members.length}
            category="budgets"
            density={sectionDensity}
            shimmer
            shimmerDelay={`${(index * 1700) % 12000}ms`}
            actions={headerActions}
            onAdd={canEdit ? () => openCreate(budgetCategory ? budgetCategory.budgetCategoryId : null) : undefined}
            addButtonProps={{
              'aria-label': interpolate(strings['budgetCategories.addAria'], { name }),
              // The guide's budget-fab anchor: the first card's [+] on sm+; xs
              // carries it on the ActionBar button instead.
              ...(index === 0 && !isMobile ? { 'data-tour-id': 'budget-fab' } : {}),
            }}
            sx={{
              // Header controls reveal on hover or focus, like row actions.
              '@media (hover: hover)': {
                '& .br-card-actions': { opacity: 0, transition: `opacity ${ds.motion.fast} ${ds.motion.easeOut}` },
                '&:hover .br-card-actions, &:focus-within .br-card-actions': { opacity: 1 },
              },
              // Drop-target highlight (section 8.16): interactive edge + tint.
              ...(isOver && {
                borderColor: palette.interactive,
                backgroundColor: palette.tint.interactive,
              }),
            }}
          >
            {rows}
          </SectionContainer>
        )}
      </DroppableCard>
    );
  };

  const typeSections = [];
  if (pendingIncomeBudgets.length > 0) {
    typeSections.push({
      weight: cardWeight(pendingIncomeBudgets.length),
      node: (
        <Box key="pending-income" data-budget-group={3}>
          <SectionContainer
            title={withAcronym('Pending Income', budgetAcronymOf(pendingIncomeBudgets[0]))}
            count={pendingIncomeBudgets.length}
            category="income"
            density={sectionDensity}
            shimmer
            shimmerDelay="6000ms"
            subtotal={
              balancesLoading
                ? <BalanceSkeleton width="70px" />
                : renderAmount(pendingIncomeTotal, 'subtotal')
            }
          >
            {pendingIncomeBudgets.map((b) => renderBudgetRow(b, 'income', shimmerIndex++, {}))}
          </SectionContainer>
        </Box>
      ),
    });
  }
  if (feBudget) {
    typeSections.push({
      weight: cardWeight(1),
      node: (
        <Box key="planned-expenses" data-budget-group={2}>
          <SectionContainer
            title={withAcronym('Planned Expenses', budgetAcronymOf(feBudget))}
            category="planned"
            density={sectionDensity}
            note="isolated from totals"
          >
            {renderBudgetRow(feBudget, 'planned', shimmerIndex++, {
              rowProps: { 'data-tour-id': 'budgets-fe-total' },
            })}
          </SectionContainer>
        </Box>
      ),
    });
  }

  const draggedBudget = activeDragId ? budgets.find((b) => b.budgetId === activeDragId) : null;
  const ArchivingIcon = resolveBudgetIcon(archiveDialog.budget?.icon);
  // Fly-back on a miss (and settle on a hit): ease-out at the slow duration,
  // no spring, because the row is data (section 6.2). Reduced motion snaps.
  const dropAnimation = prefersReducedMotion ? null : {
    duration: parseInt(ds.motion.slow, 10),
    easing: ds.motion.easeOut,
    sideEffects: defaultDropAnimationSideEffects({ styles: { active: { opacity: '0' } } }),
  };

  // Masonry: type sections seed the last column, then each card (in name
  // order) goes to the shortest column, leftmost on a tie. Once placed, a card
  // keeps its column for this page view (#1964), so a drop never moves a card
  // to another column; a new card takes the shortest.
  const columnCount = isMobile ? 1 : isWide ? 3 : 2;
  const columns = Array.from({ length: columnCount }, () => ({ weight: 0, items: [] }));
  const lastColumn = columns[columnCount - 1];
  if (columnCount > 1) {
    typeSections.forEach((t) => {
      lastColumn.items.push(t.node);
      lastColumn.weight += t.weight;
    });
  }
  if (stickyColumnsRef.current.count !== columnCount) {
    stickyColumnsRef.current = { count: columnCount, map: {} };
  }
  const sticky = stickyColumnsRef.current.map;
  const shortest = () => columns.reduce((min, col) => (col.weight < min.weight ? col : min), columns[0]);
  cards.forEach((card, index) => {
    const placed = sticky[card.key];
    const target = typeof placed === 'number' ? columns[placed] : shortest();
    target.items.push(cardNode(card, index));
    target.weight += cardWeight(card.members.length);
    sticky[card.key] = columns.indexOf(target);
  });
  // Default is a drop target even while hidden: during a drag it joins the end
  // of the shortest column, so no card above it moves.
  if (activeDragId && !cards.some((c) => c.key === 'default')) {
    const target = typeof sticky.default === 'number' ? columns[sticky.default] : shortest();
    target.items.push(cardNode({ key: 'default', name: strings['budgetCategories.default'], budgetCategory: null, members: [], monthly: [], planning: [] }, cards.length));
    sticky.default = columns.indexOf(target);
  }
  if (columnCount === 1) {
    typeSections.forEach((t) => lastColumn.items.push(t.node));
  }

  return (
    <Box sx={{ pb: 8 }}>
      <PageHero
        label="Budgets"
        back={{ label: 'Dashboard', onClick: () => navigate('/') }}
        aux={totalAux}
      />

      {error && (
        <Alert severity="error" sx={{ mt: 3, mb: 1 }}>
          {error}
        </Alert>
      )}

      {isEmpty ? (
        <Box
          sx={{
            mt: 3,
            backgroundColor: palette.surface.raised,
            border: `1px solid ${palette.divider}`,
            borderRadius: `${ds.radius.container}px`,
            padding: `${ds.space[8]}px ${ds.space[6]}px`,
          }}
        >
          <Typography sx={{ fontSize: `${ds.font.h1}px`, color: palette.text.primary, mb: 1 }}>
            No budgets found
          </Typography>
          <Typography sx={{ fontSize: `${ds.font.row}px`, color: palette.text.dim }}>
            {canEdit
              ? 'Create your first budget to start tracking your finances.'
              : 'No budgets exist in this workspace yet.'}
          </Typography>
          {canEdit && !isMobile && (
            <Button
              variant="contained"
              color="primary"
              data-tour-id="budget-fab"
              startIcon={<IconPlus size={16} />}
              onClick={() => openCreate(null)}
              sx={{ mt: 2 }}
            >
              Add budget
            </Button>
          )}
        </Box>
      ) : (
        <SectionErrorBoundary title="Budget List Error">
          {/* #1963 masonry (screen 7a-1): three columns from md, two on sm,
              one on phones. Pending Income and Planned Expenses are pinned at
              the top of the last column; each card then fills the shortest
              column. Phones list the cards first and the type sections after.
              data-budget-group marks the two type sections for the layout
              tests; the top-bar Pending Income link lands here with no
              scroll (#2179), the card being pinned at the top.
              #1964: an editor's held Normal row drags onto any category card
              (DESIGN_SYSTEM.md section 8.16). */}
          <DndContext
            sensors={sensors}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={() => setActiveDragId(null)}
          >
            <Box
              sx={{
                mt: 3,
                display: 'grid',
                gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))`,
                gap: '16px',
                alignItems: 'start',
              }}
            >
              {columns.map((column, i) => (
                <Box
                  key={i}
                  sx={{ display: 'flex', flexDirection: 'column', gap: '16px', minWidth: 0 }}
                >
                  {column.items}
                </Box>
              ))}
            </Box>
            {/* The lifted row: an overlay surface (section 2.7). On a miss it
                flies back to its origin; reduced motion snaps it instead. */}
            <DragOverlay dropAnimation={dropAnimation}>
              {draggedBudget ? (
                <Box
                  sx={{
                    backgroundColor: palette.surface.overlay,
                    border: `1px solid ${palette.overlay.border}`,
                    boxShadow: palette.overlay.shadow,
                    borderRadius: `${ds.radius.container}px`,
                    padding: '0 14px',
                    cursor: 'grabbing',
                  }}
                >
                  {renderBudgetRow(draggedBudget, 'budgets', 0, {
                    size: isWide ? 'compact' : 'default',
                    overlay: true,
                  })}
                </Box>
              ) : null}
            </DragOverlay>
          </DndContext>

          {archivedBudgets.length > 0 && (
            <Box sx={{ mt: 5 }}>
              <SectionContainer
                title="Archived"
                count={archivedBudgets.length}
                category="budgets"
                collapsible
                defaultCollapsed
                recessed
                density={isMobile ? 'compact' : 'default'}
                subtotal={
                  balancesLoading
                    ? <BalanceSkeleton width="70px" />
                    : renderAmount(archivedTotal, 'subtotal')
                }
                note="restore returns to active"
              >
                <Typography
                  sx={{
                    fontSize: `${ds.font.dim}px`,
                    color: palette.text.dim,
                    paddingTop: `${ds.space[2]}px`,
                    paddingBottom: `${ds.space[2]}px`,
                  }}
                >
                  Archived budgets retain their line history. Click a row to view detail, or restore to bring it back to the active list.
                </Typography>
                {archivedBudgets.map(renderArchivedRow)}
              </SectionContainer>
            </Box>
          )}
        </SectionErrorBoundary>
      )}

      {/* v4 (#1377): the FAB is retired - xs gets the pinned ActionBar with a
          full-width add button; sm+ uses the General section's [+]. */}
      {canEdit && isMobile && (
        <ActionBar hint={strings['budgetCategories.dragHint']}>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            aria-label="add budget"
            data-tour-id="budget-fab"
            startIcon={<IconPlus size={16} />}
            onClick={() => openCreate(null)}
          >
            Add budget
          </Button>
        </ActionBar>
      )}

      {/* Create Budget Dialog */}
      <Dialog open={createDialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Budget</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Budget Name"
            fullWidth
            variant="outlined"
            value={newBudget.name}
            onChange={(e) => updateField('name', e.target.value)}
            onBlur={() => touchField('name')}
            error={!!validationErrors.name}
            helperText={validationErrors.name || 'Enter a unique name for your budget'}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth variant="outlined" error={!!validationErrors.type} data-tour-id="budget-type-select">
            <InputLabel>Budget Type</InputLabel>
            <Select
              value={newBudget.type}
              onChange={(e) => updateField('type', e.target.value)}
              onBlur={() => touchField('type')}
              label="Budget Type"
            >
              <MenuItem value={0}>General</MenuItem>
              <MenuItem value={2} disabled={usedSingularTypes.has(2)}>
                Planned Expenses{usedSingularTypes.has(2) ? ' (already exists)' : ''}
              </MenuItem>
              <MenuItem value={3}>
                Pending Income
              </MenuItem>
            </Select>
            {validationErrors.type ? (
              <FormHelperText>{validationErrors.type}</FormHelperText>
            ) : (
              usedSingularTypes.has(2) && (
                <FormHelperText>
                  Only one Planned Expenses budget is allowed per workspace.
                </FormHelperText>
              )
            )}
          </FormControl>
          {newBudget.type === 0 && presetCategory && (
            <Typography sx={{ mt: 1.5, fontSize: `${ds.font.dim}px`, color: palette.text.dim }}>
              {interpolate(strings['budgetCategories.categoryCaption'], { name: presetCategory.name })}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleCreateBudget}
            variant="contained"
            data-tour-id="budget-create-btn"
            disabled={creating || !isValid}
          >
            {creating ? <InlineLoader message="Creating..." size={16} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Budget Dialog (#1963): name, plus the Category select for Normal
          budgets. The select is the keyboard and phone path for a move, and
          stays once drag ships in #1964. Normal budgets also get the Horizon
          control (#2141): a two-way Segmented (DESIGN_SYSTEM.md 8.9) with
          the active option's note under it. */}
      <HorizonSwitchDialog
        budget={horizonBudget}
        onClose={() => setHorizonBudget(null)}
        onSwitched={() => { setHorizonBudget(null); invalidateBudgetsCache(); loadBudgets(); }}
      />

      <Dialog open={editDialog.open} onClose={editDialog.submitting ? undefined : closeEdit} maxWidth="sm" fullWidth>
        <DialogTitle>{strings['budgetCategories.editBudgetTitle']}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Budget Name"
            fullWidth
            variant="outlined"
            value={editDialog.newName}
            onChange={(e) => setEditDialog(prev => ({ ...prev, newName: e.target.value, error: '' }))}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !editDialog.submitting) {
                e.preventDefault();
                handleEdit();
              }
            }}
            error={!!editDialog.error}
            helperText={editDialog.error || ' '}
            disabled={editDialog.submitting}
          />
          {editDialog.budget?.type === 0 && (
            <>
              <FormControl fullWidth variant="outlined" sx={{ mt: 1 }}>
                <InputLabel id="edit-budget-category-label">{strings['budgetCategories.categoryLabel']}</InputLabel>
                <Select
                  labelId="edit-budget-category-label"
                  value={editDialog.budgetCategoryId}
                  onChange={(e) => setEditDialog(prev => ({ ...prev, budgetCategoryId: e.target.value, error: '' }))}
                  label={strings['budgetCategories.categoryLabel']}
                  disabled={editDialog.submitting}
                >
                  <MenuItem value={DEFAULT_OPTION}>{strings['budgetCategories.default']}</MenuItem>
                  {[...budgetCategories].sort(byName).map((c) => (
                    <MenuItem key={c.budgetCategoryId} value={c.budgetCategoryId}>{c.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <Typography component="span" sx={{ fontSize: '13px', color: palette.text.primary }}>
                  {strings['budgets.horizonLabel']}
                </Typography>
                <Segmented
                  inCard
                  ariaLabel={strings['budgets.horizonLabel']}
                  options={[
                    { value: 'monthly', label: strings['budgets.horizonMonthly'] },
                    { value: 'planningAhead', label: strings['budgets.horizonPlanningAhead'] }
                  ]}
                  value={editDialog.horizon}
                  onChange={(value) => setEditDialog(prev => ({ ...prev, horizon: value, error: '' }))}
                  disabled={editDialog.submitting}
                />
                <FormHelperText sx={{ mx: 0 }}>
                  {editDialog.horizon === 'planningAhead'
                    ? strings['budgets.horizonPlanningAheadNote']
                    : strings['budgets.horizonMonthlyNote']}
                </FormHelperText>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEdit} disabled={editDialog.submitting}>
            {strings['budgetCategories.cancel']}
          </Button>
          <Button
            onClick={handleEdit}
            variant="contained"
            disabled={editDialog.submitting || !(editDialog.newName || '').trim()}
          >
            {editDialog.submitting
              ? <InlineLoader message="Saving..." size={16} />
              : strings['budgetCategories.save']}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Category name dialog: create or rename (#1963). */}
      <Dialog
        open={categoryDialog.open}
        onClose={categoryDialog.submitting ? undefined : closeCategoryDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {categoryDialog.budgetCategory
            ? strings['budgetCategories.renameTitle']
            : strings['budgetCategories.createTitle']}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={strings['budgetCategories.nameLabel']}
            fullWidth
            variant="outlined"
            value={categoryDialog.name}
            inputProps={{ maxLength: 100 }}
            onChange={(e) => setCategoryDialog(prev => ({ ...prev, name: e.target.value, error: '' }))}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !categoryDialog.submitting) {
                e.preventDefault();
                handleSaveCategory();
              }
            }}
            error={!!categoryDialog.error}
            helperText={categoryDialog.error || ' '}
            disabled={categoryDialog.submitting}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCategoryDialog} disabled={categoryDialog.submitting}>
            {strings['budgetCategories.cancel']}
          </Button>
          <Button
            onClick={handleSaveCategory}
            variant="contained"
            disabled={categoryDialog.submitting || !(categoryDialog.name || '').trim()}
          >
            {categoryDialog.submitting
              ? <InlineLoader message="Saving..." size={16} />
              : categoryDialog.budgetCategory
                ? strings['budgetCategories.save']
                : strings['budgetCategories.create']}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete category confirmation (#1963). Esc and the backdrop only
          cancel; nothing destructive is bound to a key. */}
      <Dialog
        open={deleteCategoryDialog.open}
        onClose={deleteCategoryDialog.submitting ? undefined : closeDeleteCategory}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {interpolate(strings['budgetCategories.deleteTitle'], { name: deleteCategoryDialog.budgetCategory?.name || '' })}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {interpolate(strings['budgetCategories.deleteBody'], { count: deleteCategoryDialog.memberCount })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteCategory} disabled={deleteCategoryDialog.submitting}>
            {strings['budgetCategories.cancel']}
          </Button>
          <Button
            onClick={handleDeleteCategory}
            variant="contained"
            color="error"
            disabled={deleteCategoryDialog.submitting}
          >
            {deleteCategoryDialog.submitting
              ? <InlineLoader message="Deleting..." size={16} />
              : strings['budgetCategories.delete']}
          </Button>
        </DialogActions>
      </Dialog>

      <ArchiveDialog
        kind="budget"
        item={archiveDialog.budget}
        balance={archiveDialog.budget ? balanceView(archiveDialog.budget).displayBalance : 0}
        icon={<ArchivingIcon />}
        category={archiveDialog.category}
        submitting={archiveDialog.submitting}
        onCancel={closeArchive}
        onConfirm={handleArchive}
      />
    </Box>
  );
};

export default Budgets;
